import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, voidRecord } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, StatusBadge, Modal, Field, inputCls, PrimaryButton, EmptyState } from "@/components/lims";
import SampleQrButton from "@/components/SampleQr";
import { canManageOrders, canDeleteOrders } from "@/lib/roles";
import { saveDownload } from "@/lib/reporting";
import { Plus, ClipboardText, Trash, ArrowRight, Lightning, CaretDown, CaretRight, DownloadSimple, LinkSimple, Signature } from "@phosphor-icons/react";

// Lab-side wording for the client requisition signature states (issue #125).
const SIGNATURE_LABELS = {
  pending: "Requisition sent for signature",
  signed: "Requisition signed",
  declined: "Signature declined",
  expired: "Signature request expired",
};

const PRIORITIES = ["Routine", "Urgent", "STAT"];
const PRIORITY_STYLE = {
  Routine: "text-slate-500",
  Urgent: "text-status-warn font-medium",
  STAT: "text-status-critical font-semibold",
};

export default function Orders() {
  const { user } = useAuth();
  const canManage = canManageOrders(user);
  const canDelete = canDeleteOrders(user);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", project: "", po_number: "", priority: "Routine", due_date: "", requested_tests: [], notes: "" });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/orders");
    setOrders(data);
    setLoading(false);
  };
  const loadRefs = async () => {
    const [c, cat] = await Promise.all([api.get("/clients"), api.get("/catalogue?active_only=true")]);
    setClients(c.data); setCatalogue(cat.data);
  };
  useEffect(() => { load(); loadRefs(); }, []);

  const openNew = () => { setForm({ client_id: "", project: "", po_number: "", priority: "Routine", due_date: "", requested_tests: [], notes: "" }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleAssay = (a) => {
    const exists = form.requested_tests.find((t) => t.assay_id === a.id);
    setForm({
      ...form,
      requested_tests: exists
        ? form.requested_tests.filter((t) => t.assay_id !== a.id)
        : [...form.requested_tests, { assay_id: a.id, assay_name: a.name }],
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/orders", form);
      toast.success("Order created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const transition = async (o, status) => {
    let reason;
    if (["Revision Required", "Rejected"].includes(status)) {
      reason = window.prompt(`Reason for ${status.toLowerCase()} (required):`);
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("A reason is required");
        return;
      }
    }
    try {
      await api.patch(`/orders/${o.id}/status`, { status, reason });
      toast.success(`Order ${o.order_code} → ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const toggleDetails = async (order) => {
    if (expanded[order.id]) {
      setExpanded({ ...expanded, [order.id]: false });
      return;
    }
    setExpanded({ ...expanded, [order.id]: true });
    if (details[order.id]) return;
    try {
      const { data } = await api.get(`/orders/${order.id}`);
      setDetails((current) => ({ ...current, [order.id]: data }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setExpanded((current) => ({ ...current, [order.id]: false }));
    }
  };

  const remove = async (o) => {
    if (!(await voidRecord(`/orders/${o.id}`, o.order_code))) return;
    toast.success("Order voided"); load();
  };

  const requestSignature = async (o) => {
    try {
      await api.post(`/orders/${o.id}/request-signature`);
      toast.success(`Requisition for ${o.order_code} sent to the client for signature`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const copySignLink = async (o) => {
    await navigator.clipboard.writeText(o.requisition_signature.sign_url);
    toast.success("Signing link copied — share it with the client");
  };
  const downloadRequisition = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.id}/signed-requisition.pdf`, { responseType: "blob" });
      saveDownload(data, `requisition-${o.order_code}.pdf`, "application/pdf");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const filtered = statusFilter ? orders.filter((o) => o.status === statusFilter) : orders;

  return (
    <Page>
      <PageHeader title="Orders" subtitle="Client work requests and their lifecycle"
        action={canManage && clients.length > 0 && <PrimaryButton data-testid="new-order-button" onClick={openNew}><Plus size={18} /> New Order</PrimaryButton>} />

      {clients.length === 0 && (
        <div className="mb-4 rounded-md bg-status-warn/10 border border-status-warn/30 px-4 py-3 text-sm text-status-warn">
          Add a client first (Clients page) before creating an order.
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <select aria-label="Filter orders by status" data-testid="order-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} max-w-[200px]`}>
          <option value="">All statuses</option>
          {["Draft", "Submitted", "Revision Required", "Accepted", "In Progress", "On Hold", "Partially Reported", "Completed", "Cancelled", "Rejected"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={ClipboardText} title="No orders" subtitle="Create an order to group samples and requested tests" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Code</th>
                <th className="px-5 py-3 font-semibold">Client</th>
                <th className="px-5 py-3 font-semibold">Project</th>
                <th className="px-5 py-3 font-semibold">Priority</th>
                <th className="px-5 py-3 font-semibold">Tests</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Advance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <Fragment key={o.id}>
                <tr data-testid={`order-row-${o.order_code}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 font-mono text-xs text-slate-500"><button aria-label={`${expanded[o.id] ? "Collapse" : "Expand"} ${o.order_code}`} onClick={() => toggleDetails(o)} className="inline-flex items-center gap-2 hover:text-slate-900">{expanded[o.id] ? <CaretDown size={14} /> : <CaretRight size={14} />}{o.order_code}</button></td>
                  <td className="px-5 py-3 font-medium text-slate-900">{o.client_name}</td>
                  <td className="px-5 py-3 text-slate-600">{o.project || "—"}</td>
                  <td className="px-5 py-3"><span className={`text-xs flex items-center gap-1 ${PRIORITY_STYLE[o.priority]}`}>{o.priority === "STAT" && <Lightning size={12} weight="fill" />}{o.priority}</span></td>
                  <td className="px-5 py-3 text-slate-600"><span title="Requested tests">{o.requested_tests?.length || 0} requested</span><span className="block text-xs text-slate-400">{o.sample_count || 0} samples · {o.test_count || 0} tests</span></td>
                  <td className="px-5 py-3">
                    <StatusBadge status={o.status} />
                    {o.requisition_signature && (
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                        <span>{SIGNATURE_LABELS[o.requisition_signature.status] || o.requisition_signature.status}</span>
                        {o.requisition_signature.sign_url && (
                          <button title="Copy signing link" onClick={() => copySignLink(o)} className="p-1 rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100"><LinkSimple size={14} /></button>
                        )}
                        {o.requisition_signature.has_document && (
                          <button title="Download signed requisition" onClick={() => downloadRequisition(o)} className="p-1 rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100"><DownloadSimple size={14} /></button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      {canManage && (!o.requisition_signature || ["declined", "expired"].includes(o.requisition_signature.status)) && (
                        <button title="Ask the client to sign this requisition" onClick={() => requestSignature(o)}
                          className="text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors flex items-center gap-1">
                          <Signature size={12} /> Request signature
                        </button>
                      )}
                      {(o.allowed_transitions || []).map((t) => (
                        <button key={t} data-testid={`advance-${o.order_code}-${t}`} onClick={() => transition(o, t)}
                          className="text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors flex items-center gap-1">
                          {t} <ArrowRight size={11} />
                        </button>
                      ))}
                      {canDelete && <button onClick={() => remove(o)} className="p-1.5 rounded-md text-slate-400 hover:text-status-critical hover:bg-status-critical/10 transition-colors"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
                {expanded[o.id] && (
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    <td colSpan={7} className="px-8 py-5">
                      {!details[o.id] ? <div className="h-16 rounded bg-slate-100 animate-pulse" /> : details[o.id].samples.length === 0 ? (
                        <p className="text-sm text-slate-500">No samples have been accessioned against this order yet.</p>
                      ) : (
                        <div className="space-y-4" data-testid={`order-hierarchy-${o.order_code}`}>
                          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Order → samples → tests</div>
                          {details[o.id].samples.map((sample) => (
                            <div key={sample.id} className="rounded-lg border border-slate-200 bg-white">
                              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                                <div><span className="font-mono text-xs text-blue-700">{sample.sample_code}</span><span className="ml-3 font-medium text-slate-900">{sample.name || sample.sample_type}</span><span className="ml-2 text-xs text-slate-400">{sample.sample_type}</span></div>
                                <div className="flex items-center gap-2">
                                  <SampleQrButton sampleId={sample.id} sampleCode={sample.sample_code} />
                                  <StatusBadge status={sample.status} />
                                </div>
                              </div>
                              {sample.tests.length === 0 ? <p className="px-4 py-3 text-xs text-slate-400">No tests linked</p> : (
                                <table className="w-full text-xs">
                                  <thead className="text-left uppercase text-slate-400"><tr><th className="px-4 py-2">Test ID</th><th className="px-4 py-2">Assay</th><th className="px-4 py-2">Analyst</th><th className="px-4 py-2">Plan</th><th className="px-4 py-2">Status</th></tr></thead>
                                  <tbody className="divide-y divide-slate-100">{sample.tests.map((test) => <tr key={test.id}><td className="px-4 py-2 font-mono text-slate-500">{test.test_code}</td><td className="px-4 py-2 font-medium">{test.assay_name}</td><td className="px-4 py-2 text-slate-500">{test.assigned_to || "Unassigned"}</td><td className="px-4 py-2 text-slate-500">{test.planned_start ? new Date(test.planned_start).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "Not scheduled"}</td><td className="px-4 py-2"><StatusBadge status={test.status} /></td></tr>)}</tbody>
                                </table>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Order" testid="order-modal">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Client">
            <select data-testid="order-client-select" required value={form.client_id} onChange={set("client_id")} className={inputCls}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.client_code} · {c.legal_name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Project"><input value={form.project} onChange={set("project")} className={inputCls} /></Field>
            <Field label="PO number"><input value={form.po_number} onChange={set("po_number")} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Priority"><select value={form.priority} onChange={set("priority")} className={inputCls}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></Field>
            <Field label="Due date"><input type="date" value={form.due_date} onChange={set("due_date")} className={inputCls} /></Field>
          </div>
          <Field label="Requested tests">
            {catalogue.length === 0 ? (
              <p className="text-xs text-slate-400">No assays in catalogue yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="order-assay-picker">
                {catalogue.map((a) => {
                  const sel = form.requested_tests.find((t) => t.assay_id === a.id);
                  return (
                    <button type="button" key={a.id} onClick={() => toggleAssay(a)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${sel ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                      {a.name}
                    </button>
                  );
                })}
              </div>
            )}
          </Field>
          <Field label="Notes"><textarea value={form.notes} onChange={set("notes")} rows={2} className={inputCls} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <PrimaryButton data-testid="save-order-button" disabled={saving}>Create Order</PrimaryButton>
          </div>
        </form>
      </Modal>
    </Page>
  );
}
